import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

export async function listConversations() {
  const { data, error } = await supabase
    .from("legal_conversations")
    .select("*")
    .is("deleted_at", null)
    .order("updated_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createConversation(title = "Nova conversa jurídica") {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const { data, error } = await supabase
    .from("legal_conversations")
    .insert({ title, user_id: user.id })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteConversation(id: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  // DELETAR ARQUIVOS PRIMEIRO
  const { error: filesError } = await supabase
    .from('legal_chat_files')
    .delete()
    .eq('conversation_id', id)
    .eq('user_id', user.id);

  if (filesError) {
    console.error('❌ Error deleting files:', filesError);
  }

  // Soft delete da conversa
  const { error } = await supabase.rpc(
    "safe_soft_delete_legal_conversation",
    { p_id: id }
  );
  if (error) {
    throw new Error(error.message || "Falha ao excluir conversa");
  }
}

export async function loadMessages(conversationId: string) {
  const { data, error } = await supabase
    .from("legal_messages")
    .select("id, role, content, created_at, metadata")
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true });

  if (error) throw error;
  return (data || []).map(msg => ({
    role: msg.role as "user" | "assistant",
    content: msg.content,
    id: msg.id,
    created_at: msg.created_at,
    metadata: msg.metadata as Record<string, any> | null
  }));
}

// Upload files to Supabase Storage
export async function uploadFilesToStorage(
  conversationId: string, 
  files: File[]
): Promise<string[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  
  const uploadedUrls: string[] = [];
  
  for (const file of files) {
    const filename = `${conversationId}/${Date.now()}_${file.name}`;
    const { data, error } = await supabase.storage
      .from('legal-chat-files')
      .upload(filename, file);
    
    if (error) {
      console.error('Upload error:', error);
      continue;
    }
    
    const { data: { publicUrl } } = supabase.storage
      .from('legal-chat-files')
      .getPublicUrl(data.path);
    
    uploadedUrls.push(publicUrl);
  }
  
  return uploadedUrls;
}

export async function sendAssistantMessage({
  conversationId,
  text,
}: {
  conversationId: string;
  text: string;
}) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error("Auth required");

  const res = await fetch(`${FUNCTIONS_URL}/assistant-chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      conversation_id: conversationId,
      message: text,
    }),
  });

  if (res.status === 429) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.details?.message || "Limite de créditos atingido.");
  }

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || "Erro ao enviar mensagem");
  }

  return await res.json();
}

export async function sendMessageSSE({
  conversationId,
  text,
  useWeb,
  files = [],
}: {
  conversationId: string;
  text: string;
  useWeb: boolean;
  files?: File[];
}) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error("Auth required");

  // PROCESSAMENTO DE ARQUIVOS - EXTRAIR TEXTO
  let processedFiles: any[] = [];
  let combinedExtractedText = "";
  
  if (files.length > 0) {
    console.log(`📎 Processing ${files.length} files for legal chat...`);
    
    const { processLegalChatFile } = await import("@/services/legalChatFileProcessor");
    
    for (const file of files) {
      try {
        const result = await processLegalChatFile(
          conversationId,
          null,
          file
        );
        
        processedFiles.push({
          file_id: result.file_id,
          filename: file.name,
          document_type: result.document_type,
          extraction_quality: result.quality
        });
        
        console.log(`✅ File processed: ${file.name} (${result.text_length} chars, summary_added: ${result.summary_added})`);
        
      } catch (error) {
        console.error(`❌ Failed to process file ${file.name}:`, error);
        toast({
          title: "Erro ao Processar Arquivo",
          description: `Falha ao processar ${file.name}. Continuando com outros arquivos.`,
          variant: "destructive"
        });
      }
    }
  }

  // COMBINAR TEXTO DA MENSAGEM COM TEXTO EXTRAÍDO
  const fullMessage = combinedExtractedText 
    ? `${text}\n\n=== DOCUMENTOS ANEXADOS ===\n${combinedExtractedText}`
    : text;

  const res = await fetch(`${FUNCTIONS_URL}/legalchat-complete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      conversation_id: conversationId,
      message: fullMessage,
      use_web: useWeb,
      processed_files: processedFiles
    }),
  });

  // Tratamento específico para limite de créditos
  if (res.status === 429) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.details?.message || "Limite de créditos atingido. Considere fazer upgrade do plano.");
  }

  if (!res.ok || !res.body) throw new Error("SSE error");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();

  return {
    async *stream() {
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";
        for (const part of parts) {
          const lines = part.split("\n");
          const eventLine = lines.find((l) => l.startsWith("event:"));
          const dataLine = lines.find((l) => l.startsWith("data:"));
          const evt = eventLine?.replace("event: ", "").trim() || "";
          const data = dataLine?.replace("data: ", "").trim() || "";
          yield { evt, data };
        }
      }
    },
  };
}

export async function renameConversation(id: string, newTitle: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const { error } = await supabase
    .from("legal_conversations")
    .update({ title: newTitle, updated_at: new Date().toISOString() })
    .eq("id", id)
    .eq("user_id", user.id);

  if (error) throw error;
}

export async function generateConversationTitle(conversationId: string, firstMessage: string) {
  // Limpar texto (remover quebras de linha, espaços múltiplos, emojis, etc)
  const cleanMessage = firstMessage
    .replace(/\n+/g, " ")           // Substituir quebras de linha por espaço
    .replace(/\s+/g, " ")            // Remover espaços múltiplos
    .replace(/[📎🔗⚠️✅❌]/g, "")    // Remover emojis comuns
    .trim();
  
  // Gerar título limitado a 50 caracteres
  const title = cleanMessage.slice(0, 50) + (cleanMessage.length > 50 ? "..." : "");
  
  // Atualizar no banco de dados
  await renameConversation(conversationId, title);
}

export async function updateConversationTemplate(id: string, template: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  const { error } = await supabase
    .from("legal_conversations")
    .update({ 
      metadata: { template },
      updated_at: new Date().toISOString() 
    })
    .eq("id", id)
    .eq("user_id", user.id);

  if (error) throw error;
}
